"use client";

import { c as _c } from "react/compiler-runtime";
import type { InitialConfigType } from "@lexical/react/LexicalComposer";
import type { FocusEvent } from "react";
import { LexicalComposer } from "@lexical/react/LexicalComposer";
import { forwardRef, useImperativeHandle, useCallback, useEffect, useRef } from "react";
import type { EditorState } from "lexical";
import { RootNode, COMMAND_PRIORITY_CRITICAL, COMMAND_PRIORITY_HIGH, KEY_ARROW_UP_COMMAND, createCommand } from "lexical";
import { RichTextPlugin } from "@lexical/react/LexicalRichTextPlugin";
import { ContentEditable } from "@lexical/react/LexicalContentEditable";
import LexicalErrorBoundary from "@lexical/react/LexicalErrorBoundary";
import { HistoryPlugin } from "@lexical/react/LexicalHistoryPlugin";
import { OnChangePlugin } from "@lexical/react/LexicalOnChangePlugin";
import { MarkdownShortcutPlugin } from "@lexical/react/LexicalMarkdownShortcutPlugin";
import { TabIndentationPlugin } from "@lexical/react/LexicalTabIndentationPlugin";
import { ListNode, ListItemNode } from "@lexical/list";
import { HeadingNode, QuoteNode } from "@lexical/rich-text";
import { HorizontalRuleNode } from "@lexical/react/LexicalHorizontalRuleNode";
import { ListPlugin } from "@lexical/react/LexicalListPlugin";
import { TableCellNode, TableNode, TableRowNode } from "@lexical/table";
import { CodeHighlightNode, CodeNode } from "@lexical/code";
import { AutoLinkNode, LinkNode } from "@lexical/link";
import { LinkPlugin } from "@lexical/react/LexicalLinkPlugin";
import type { MentionedUser, MentionedWave, ReferencedNft } from "@/entities/IDrop";
import { ActiveDropAction } from "@/types/dropInteractionTypes";
import { MentionNode } from "../drops/create/lexical/nodes/MentionNode";
import { GroupMentionNode } from "../drops/create/lexical/nodes/GroupMentionNode";
import { HashtagNode } from "../drops/create/lexical/nodes/HashtagNode";
import { WaveMentionNode } from "../drops/create/lexical/nodes/WaveMentionNode";
import { ImageNode } from "../drops/create/lexical/nodes/ImageNode";
import ExampleTheme from "../drops/create/lexical/ExampleTheme";
import { assertUnreachable } from "@/helpers/AllowlistToolHelpers";
import type { ClearEditorPluginHandles } from "../drops/create/lexical/plugins/ClearEditorPlugin";
import ClearEditorPlugin from "../drops/create/lexical/plugins/ClearEditorPlugin";
import type { NewMentionsPluginHandles } from "../drops/create/lexical/plugins/mentions/MentionsPlugin";
import NewMentionsPlugin from "../drops/create/lexical/plugins/mentions/MentionsPlugin";
import type { NewHastagsPluginHandles } from "../drops/create/lexical/plugins/hashtags/HashtagsPlugin";
import NewHashtagsPlugin from "../drops/create/lexical/plugins/hashtags/HashtagsPlugin";
import type { NewWaveMentionsPluginHandles } from "../drops/create/lexical/plugins/waves/WaveMentionsPlugin";
import NewWaveMentionsPlugin from "../drops/create/lexical/plugins/waves/WaveMentionsPlugin";
import { MaxLengthPlugin } from "../drops/create/lexical/plugins/MaxLengthPlugin";
import DragDropPastePlugin from "../drops/create/lexical/plugins/DragDropPastePlugin";
import EnterKeyPlugin from "../drops/create/lexical/plugins/enter/EnterKeyPlugin";
import { useLexicalComposerContext } from "@lexical/react/LexicalComposerContext";
import CreateDropEmojiPicker from "./CreateDropEmojiPicker";
import useCapacitor from "@/hooks/useCapacitor";
import EmojiPlugin from "../drops/create/lexical/plugins/emoji/EmojiPlugin";
import { EmojiNode } from "../drops/create/lexical/nodes/EmojiNode";
import { SAFE_MARKDOWN_TRANSFORMERS } from "@/components/drops/create/lexical/transformers/markdownTransformers";
import PlainTextPastePlugin from "@/components/drops/create/lexical/plugins/PlainTextPastePlugin";
export interface CreateDropInputHandles {
  clearEditorState: () => void;
  focus: () => void;
}

// Create a custom command
const DISABLE_EDIT_COMMAND = createCommand("DISABLE_EDIT");

// Create a custom plugin to handle disabling
function DisableEditPlugin(t0) {
  const $ = _c(4);
  const {
    disabled
  } = t0;
  const [editor] = useLexicalComposerContext();
  let t1;
  let t2;
  if ($[0] !== disabled || $[1] !== editor) {
    t1 = () => {
      if (disabled) {
        return editor.registerCommand(DISABLE_EDIT_COMMAND, _temp, COMMAND_PRIORITY_CRITICAL);
      }
      return _temp2;
    };
    t2 = [editor, disabled];
    $[0] = disabled;
    $[1] = editor;
    $[2] = t1;
    $[3] = t2;
  } else {
    t1 = $[2];
    t2 = $[3];
  }
  useEffect(t1, t2);
  return null;
}
function _temp2() {}
function _temp() {
  return true;
}
export function EditLastDropArrowUpPlugin(t0) {
  const $ = _c(12);
  const {
    canEditLastDropWithArrow,
    onRequestEditLastDrop,
    canUseArrowUpShortcut
  } = t0;
  const [editor] = useLexicalComposerContext();
  const canEditLastDropWithArrowRef = useRef(canEditLastDropWithArrow);
  const onRequestEditLastDropRef = useRef(onRequestEditLastDrop);
  const canUseArrowUpShortcutRef = useRef(canUseArrowUpShortcut);
  let t1;
  let t2;
  if ($[0] !== canEditLastDropWithArrow) {
    t1 = () => {
      canEditLastDropWithArrowRef.current = canEditLastDropWithArrow;
    };
    t2 = [canEditLastDropWithArrow];
    $[0] = canEditLastDropWithArrow;
    $[1] = t1;
    $[2] = t2;
  } else {
    t1 = $[1];
    t2 = $[2];
  }
  useEffect(t1, t2);
  let t3;
  let t4;
  if ($[3] !== onRequestEditLastDrop) {
    t3 = () => {
      onRequestEditLastDropRef.current = onRequestEditLastDrop;
    };
    t4 = [onRequestEditLastDrop];
    $[3] = onRequestEditLastDrop;
    $[4] = t3;
    $[5] = t4;
  } else {
    t3 = $[4];
    t4 = $[5];
  }
  useEffect(t3, t4);
  let t5;
  let t6;
  if ($[6] !== canUseArrowUpShortcut) {
    t5 = () => {
      canUseArrowUpShortcutRef.current = canUseArrowUpShortcut;
    };
    t6 = [canUseArrowUpShortcut];
    $[6] = canUseArrowUpShortcut;
    $[7] = t5;
    $[8] = t6;
  } else {
    t5 = $[7];
    t6 = $[8];
  }
  useEffect(t5, t6);
  let t7;
  let t8;
  if ($[9] !== editor) {
    t7 = () => editor.registerCommand(KEY_ARROW_UP_COMMAND, event => {
      if (event.ctrlKey || event.metaKey || event.altKey || event.shiftKey) {
        return false;
      }
      if (!canEditLastDropWithArrowRef.current || !onRequestEditLastDropRef.current || !canUseArrowUpShortcutRef.current()) {
        return false;
      }
      const handled = onRequestEditLastDropRef.current();
      if (!handled) {
        return false;
      }
      event.preventDefault();
      return true;
    }, COMMAND_PRIORITY_HIGH);
    t8 = [editor];
    $[9] = editor;
    $[10] = t7;
    $[11] = t8;
  } else {
    t7 = $[10];
    t8 = $[11];
  }
  useEffect(t7, t8);
  return null;
}
const CreateDropInput = forwardRef<CreateDropInputHandles, {
  readonly waveId: string;
  readonly editorState: EditorState | null;
  readonly type: ActiveDropAction | null;
  readonly canSubmit: boolean;
  readonly isStormMode: boolean;
  readonly submitting: boolean;
  readonly isDropMode: boolean;
  readonly canMentionAll?: boolean | undefined;
  readonly onDrop?: (() => void) | undefined;
  readonly onEditorState: (editorState: EditorState) => void;
  readonly onEditorBlur?: (event: FocusEvent<HTMLDivElement>) => void;
  readonly onReferencedNft: (referencedNft: ReferencedNft) => void;
  readonly onMentionedUser: (mentionedUser: Omit<MentionedUser, "current_handle">) => void;
  readonly onMentionedWave: (mentionedWave: MentionedWave) => void;
  readonly onAttachmentFiles?: ((files: File[]) => void) | undefined;
  readonly hasValidationError?: boolean | undefined;
  readonly validationHelperText?: string | null | undefined;
  readonly canEditLastDropWithArrow?: boolean | undefined;
  readonly onRequestEditLastDrop?: (() => boolean) | undefined;
}>((t0, ref) => {
  const $ = _c(89);
  const {
    waveId,
    editorState,
    type,
    canSubmit,
    isStormMode,
    isDropMode,
    canMentionAll: t1,
    submitting,
    onEditorState,
    onEditorBlur,
    onReferencedNft,
    onMentionedUser,
    onMentionedWave,
    onAttachmentFiles,
    hasValidationError: t2,
    validationHelperText: t3,
    canEditLastDropWithArrow: t4,
    onRequestEditLastDrop,
    onDrop
  } = t0;
  const canMentionAll = t1 === undefined ? false : t1;
  const hasValidationError = t2 === undefined ? false : t2;
  const validationHelperText = t3 === undefined ? null : t3;
  const canEditLastDropWithArrow = t4 === undefined ? false : t4;
  const {
    isCapacitor
  } = useCapacitor();
  let t5;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t5 = [MentionNode, GroupMentionNode, HashtagNode, WaveMentionNode, RootNode, HeadingNode, ListNode, ListItemNode, QuoteNode, CodeNode, CodeHighlightNode, TableNode, TableCellNode, TableRowNode, AutoLinkNode, LinkNode, HorizontalRuleNode, ImageNode, EmojiNode];
    $[0] = t5;
  } else {
    t5 = $[0];
  }
  const t6 = !submitting;
  let t7;
  if ($[1] !== editorState || $[2] !== t6) {
    t7 = {
      namespace: "User Drop",
      nodes: t5,
      editorState,
      editable: t6,
      onError(error) {
        throw error;
      },
      theme: ExampleTheme
    };
    $[1] = editorState;
    $[2] = t6;
    $[3] = t7;
  } else {
    t7 = $[3];
  }
  const editorConfig = t7;
  let t8;
  if ($[4] !== onEditorState) {
    t8 = editorState_0 => onEditorState(editorState_0);
    $[4] = onEditorState;
    $[5] = t8;
  } else {
    t8 = $[5];
  }
  const onEditorStateChange = t8;
  let t9;
  if ($[6] !== onMentionedUser) {
    t9 = user => {
      onMentionedUser(user);
    };
    $[6] = onMentionedUser;
    $[7] = t9;
  } else {
    t9 = $[7];
  }
  const onMentionedUserAdded = t9;
  let t10;
  if ($[8] !== onMentionedWave) {
    t10 = wave => {
      onMentionedWave(wave);
    };
    $[8] = onMentionedWave;
    $[9] = t10;
  } else {
    t10 = $[9];
  }
  const onMentionedWaveAdded = t10;
  let t11;
  if ($[10] !== onReferencedNft) {
    t11 = hashtag => onReferencedNft(hashtag);
    $[10] = onReferencedNft;
    $[11] = t11;
  } else {
    t11 = $[11];
  }
  const onHashtagAdded = t11;
  let t12;
  if ($[12] !== isDropMode || $[13] !== isStormMode || $[14] !== type) {
    t12 = () => {
      if (isStormMode) {
        return "Add to the storm";
      }
      if (type === null) {
        return isDropMode ? "Create a drop" : "Write a chat message";
      }
      switch (type) {
        case ActiveDropAction.REPLY:
          {
            return isDropMode ? "Drop a reply" : "Post a reply";
          }
        case ActiveDropAction.QUOTE:
          {
            return isDropMode ? "Quote a drop" : "Post a quote";
          }
        default:
          {
            assertUnreachable(type);
            return "";
          }
      }
    };
    $[12] = isDropMode;
    $[13] = isStormMode;
    $[14] = type;
    $[15] = t12;
  } else {
    t12 = $[15];
  }
  const getPlaceHolderText = t12;
  let t13;
  if ($[16] === Symbol.for("react.memo_cache_sentinel")) {
    const urlRegExp = new RegExp(/((([A-Za-z]{3,9}:(?:\/\/)?)(?:[-;:&=+$,\w]+@)?[A-Za-z0-9.-]+|(?:www.|[-;:&=+$,\w]+@)[A-Za-z0-9.-]+)((?:\/[+~%/.\w-_]*)?\??(?:[-+=&;%@.\w_]*)#?(?:[\w]*))?)/);
    t13 = function validateUrl(url) {
      return url === "https://" || urlRegExp.test(url);
    };
    $[16] = t13;
  } else {
    t13 = $[16];
  }
  const validateUrl = t13;
  const clearEditorRef = useRef(null);
  const editorRef = useRef(null);
  let t14;
  if ($[17] === Symbol.for("react.memo_cache_sentinel")) {
    t14 = () => {
      clearEditorRef.current?.clearEditorState();
    };
    $[17] = t14;
  } else {
    t14 = $[17];
  }
  const clearEditorState = t14;
  let t15;
  if ($[18] === Symbol.for("react.memo_cache_sentinel")) {
    t15 = () => ({
      clearEditorState,
      focus: () => {
        (editorRef.current?.querySelector("[contenteditable=\"true\"]") as HTMLElement).focus();
      }
    });
    $[18] = t15;
  } else {
    t15 = $[18];
  }
  useImperativeHandle(ref, t15);
  const mentionsPluginRef = useRef(null);
  const hashtagPluginRef = useRef(null);
  const waveMentionsPluginRef = useRef(null);
  let t16;
  if ($[19] === Symbol.for("react.memo_cache_sentinel")) {
    t16 = () => !mentionsPluginRef.current?.isMentionsOpen() && !hashtagPluginRef.current?.isHashtagsOpen() && !waveMentionsPluginRef.current?.isWaveMentionsOpen();
    $[19] = t16;
  } else {
    t16 = $[19];
  }
  const canUseShortcutKeys = t16;
  const canSubmitWithEnter = canUseShortcutKeys;
  const canSubmitRef = useRef(canSubmit);
  const onDropRef = useRef(onDrop);
  let t17;
  let t18;
  if ($[20] !== canSubmit) {
    t17 = () => {
      canSubmitRef.current = canSubmit;
    };
    t18 = [canSubmit];
    $[20] = canSubmit;
    $[21] = t17;
    $[22] = t18;
  } else {
    t17 = $[21];
    t18 = $[22];
  }
  useEffect(t17, t18);
  let t19;
  let t20;
  if ($[23] !== onDrop) {
    t19 = () => {
      onDropRef.current = onDrop;
    };
    t20 = [onDrop];
    $[23] = onDrop;
    $[24] = t19;
    $[25] = t20;
  } else {
    t19 = $[24];
    t20 = $[25];
  }
  useEffect(t19, t20);
  let t21;
  if ($[26] === Symbol.for("react.memo_cache_sentinel")) {
    t21 = () => {
      if (!canSubmitRef.current || !onDropRef.current) {
        return;
      }
      onDropRef.current();
    };
    $[26] = t21;
  } else {
    t21 = $[26];
  }
  const handleSubmit = t21;
  let t22;
  if ($[27] === Symbol.for("react.memo_cache_sentinel")) {
    t22 = {
      touchAction: "manipulation"
    };
    $[27] = t22;
  } else {
    t22 = $[27];
  }
  let t23;
  if ($[28] !== submitting) {
    t23 = e => {
      const target = e.currentTarget;
      if (!submitting) {
        Promise.resolve().then(() => {
          target.focus();
          const selection = window.getSelection();
          if (selection?.rangeCount === 0) {
            const range = document.createRange();
            range.selectNodeContents(target);
            range.collapse(false);
            selection.addRange(range);
          }
        });
      }
    };
    $[28] = submitting;
    $[29] = t23;
  } else {
    t23 = $[29];
  }
  const t24 = `editor-input-one-liner tw-form-input tw-block tw-max-h-[40vh] tw-w-full tw-resize-none tw-rounded-lg tw-border-0 tw-bg-iron-900 tw-py-2.5 tw-pl-3 tw-text-base tw-font-normal tw-leading-6 tw-text-white tw-caret-primary-400 tw-shadow-sm tw-ring-1 tw-ring-inset tw-ring-iron-700 tw-transition tw-duration-300 tw-ease-out tw-scrollbar-thin tw-scrollbar-track-iron-900 tw-scrollbar-thumb-iron-600 placeholder:tw-text-iron-500 focus:tw-bg-iron-950 focus:tw-outline-none focus:tw-ring-1 focus:tw-ring-inset focus:tw-ring-primary-400 sm:tw-text-sm ${submitting ? "tw-cursor-default tw-opacity-50" : ""} ${isCapacitor ? "tw-pr-[35px]" : "tw-pr-[40px]"}`;
  let t25;
  if ($[30] !== onEditorBlur || $[31] !== t23 || $[32] !== t24) {
    t25 = <ContentEditable spellCheck={true} autoCorrect="on" style={t22} onClick={t23} onBlur={onEditorBlur} className={t24} />;
    $[30] = onEditorBlur;
    $[31] = t23;
    $[32] = t24;
    $[33] = t25;
  } else {
    t25 = $[33];
  }
  let t26;
  if ($[34] === Symbol.for("react.memo_cache_sentinel")) {
    t26 = <CreateDropEmojiPicker />;
    $[34] = t26;
  } else {
    t26 = $[34];
  }
  let t27;
  if ($[35] !== t25) {
    t27 = <div className="tw-relative">{t25}{t26}</div>;
    $[35] = t25;
    $[36] = t27;
  } else {
    t27 = $[36];
  }
  const t28 = `editor-placeholder ${submitting ? "tw-opacity-50" : ""}`;
  const t29 = getPlaceHolderText();
  let t30;
  if ($[37] !== t28 || $[38] !== t29) {
    t30 = <span className={t28}>{t29}</span>;
    $[37] = t28;
    $[38] = t29;
    $[39] = t30;
  } else {
    t30 = $[39];
  }
  let t31;
  if ($[40] !== t27 || $[41] !== t30) {
    t31 = <RichTextPlugin contentEditable={t27} placeholder={t30} ErrorBoundary={LexicalErrorBoundary} />;
    $[40] = t27;
    $[41] = t30;
    $[42] = t31;
  } else {
    t31 = $[42];
  }
  let t32;
  if ($[43] === Symbol.for("react.memo_cache_sentinel")) {
    t32 = <HistoryPlugin />;
    $[43] = t32;
  } else {
    t32 = $[43];
  }
  let t33;
  if ($[44] !== onEditorStateChange) {
    t33 = <OnChangePlugin onChange={onEditorStateChange} />;
    $[44] = onEditorStateChange;
    $[45] = t33;
  } else {
    t33 = $[45];
  }
  let t34;
  if ($[46] !== canMentionAll || $[47] !== onMentionedUserAdded || $[48] !== waveId) {
    t34 = <NewMentionsPlugin waveId={waveId} onSelect={onMentionedUserAdded} canMentionAll={canMentionAll} ref={mentionsPluginRef} />;
    $[46] = canMentionAll;
    $[47] = onMentionedUserAdded;
    $[48] = waveId;
    $[49] = t34;
  } else {
    t34 = $[49];
  }
  let t35;
  if ($[50] !== onMentionedWaveAdded) {
    t35 = <NewWaveMentionsPlugin onSelect={onMentionedWaveAdded} ref={waveMentionsPluginRef} />;
    $[50] = onMentionedWaveAdded;
    $[51] = t35;
  } else {
    t35 = $[51];
  }
  let t36;
  if ($[52] !== onHashtagAdded) {
    t36 = <NewHashtagsPlugin onSelect={onHashtagAdded} ref={hashtagPluginRef} />;
    $[52] = onHashtagAdded;
    $[53] = t36;
  } else {
    t36 = $[53];
  }
  let t37;
  if ($[54] === Symbol.for("react.memo_cache_sentinel")) {
    t37 = <MaxLengthPlugin maxLength={25000} />;
    $[54] = t37;
  } else {
    t37 = $[54];
  }
  let t38;
  if ($[55] !== onAttachmentFiles) {
    t38 = <DragDropPastePlugin onAttachmentFiles={onAttachmentFiles} />;
    $[55] = onAttachmentFiles;
    $[56] = t38;
  } else {
    t38 = $[56];
  }
  let t39;
  let t40;
  let t41;
  let t42;
  let t43;
  let t44;
  if ($[57] === Symbol.for("react.memo_cache_sentinel")) {
    t39 = <ListPlugin />;
    t40 = <PlainTextPastePlugin />;
    t41 = <MarkdownShortcutPlugin transformers={SAFE_MARKDOWN_TRANSFORMERS} />;
    t42 = <TabIndentationPlugin />;
    t43 = <LinkPlugin validateUrl={validateUrl} />;
    t44 = <ClearEditorPlugin ref={clearEditorRef} />;
    $[57] = t39;
    $[58] = t40;
    $[59] = t41;
    $[60] = t42;
    $[61] = t43;
    $[62] = t44;
  } else {
    t39 = $[57];
    t40 = $[58];
    t41 = $[59];
    t42 = $[60];
    t43 = $[61];
    t44 = $[62];
  }
  let t45;
  let t46;
  if ($[63] !== submitting) {
    t45 = <DisableEditPlugin disabled={submitting} />;
    t46 = <EnterKeyPlugin handleSubmit={handleSubmit} canSubmitWithEnter={canSubmitWithEnter} disabled={submitting} />;
    $[63] = submitting;
    $[64] = t45;
    $[65] = t46;
  } else {
    t45 = $[64];
    t46 = $[65];
  }
  let t47;
  if ($[66] !== canEditLastDropWithArrow || $[67] !== onRequestEditLastDrop) {
    t47 = <EditLastDropArrowUpPlugin canEditLastDropWithArrow={canEditLastDropWithArrow} onRequestEditLastDrop={onRequestEditLastDrop} canUseArrowUpShortcut={canUseShortcutKeys} />;
    $[66] = canEditLastDropWithArrow;
    $[67] = onRequestEditLastDrop;
    $[68] = t47;
  } else {
    t47 = $[68];
  }
  let t48;
  if ($[69] === Symbol.for("react.memo_cache_sentinel")) {
    t48 = <EmojiPlugin />;
    $[69] = t48;
  } else {
    t48 = $[69];
  }
  let t49;
  if ($[70] !== t31 || $[71] !== t33 || $[72] !== t34 || $[73] !== t35 || $[74] !== t36 || $[75] !== t38 || $[76] !== t45 || $[77] !== t46 || $[78] !== t47) {
    t49 = <div className="tw-flex tw-items-end tw-gap-x-3"><div className="tw-relative tw-w-full">{t31}{t32}{t33}{t34}{t35}{t36}{t37}{t38}{t39}{t40}{t41}{t42}{t43}{t44}{t45}{t46}{t47}{t48}</div></div>;
    $[70] = t31;
    $[71] = t33;
    $[72] = t34;
    $[73] = t35;
    $[74] = t36;
    $[75] = t38;
    $[76] = t45;
    $[77] = t46;
    $[78] = t47;
    $[79] = t49;
  } else {
    t49 = $[79];
  }
  let t50;
  if ($[80] !== editorConfig || $[81] !== t49) {
    t50 = <LexicalComposer initialConfig={editorConfig}>{t49}</LexicalComposer>;
    $[80] = editorConfig;
    $[81] = t49;
    $[82] = t50;
  } else {
    t50 = $[82];
  }
  let t51;
  if ($[83] !== hasValidationError || $[84] !== validationHelperText) {
    t51 = hasValidationError && validationHelperText && <div role="alert" className="tw-mt-2 tw-text-xs tw-font-medium tw-text-error">{validationHelperText}</div>;
    $[83] = hasValidationError;
    $[84] = validationHelperText;
    $[85] = t51;
  } else {
    t51 = $[85];
  }
  let t52;
  if ($[86] !== t50 || $[87] !== t51) {
    t52 = <div className="tailwind-scope" ref={editorRef}>{t50}{t51}</div>;
    $[86] = t50;
    $[87] = t51;
    $[88] = t52;
  } else {
    t52 = $[88];
  }
  return t52;
});
CreateDropInput.displayName = "CreateDropInput";
export default CreateDropInput;